import axios from 'axios';
import qs from 'qs';

// Strapi API base URL. Dev (`npm start`): .env.development → http://localhost:1337/api.
// Production build: REACT_APP_API_URL in .env or .env.production. If unset: `/api` + package.json proxy.
const API_URL =
  process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ''
    ? process.env.REACT_APP_API_URL
    : '/api';

export const STRAPI_BASE_URL =
  process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ''
    ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
    : '';

/**
 * Strapi 5: `populate=*` підтягує dynamic zone лише поверхнево; медіа в `shared.media` / `shared.slider`
 * без явного `populate[blocks][on][…]` часто приходять без `url` — на сайті здається «немає зображення».
 */
const ARTICLE_REST_POPULATE = {
  cover: true,
  author: { populate: ['avatar'] },
  categories: true,
  tags: true,
  blocks: {
    on: {
      'shared.media': { populate: ['file'] },
      'shared.slider': { populate: ['files'] },
      'shared.rich-text': true,
      'shared.quote': true,
    },
  },
};

const PAGE_REST_POPULATE = {
  blocks: {
    on: {
      'shared.media': { populate: ['file'] },
      'shared.slider': { populate: ['files'] },
      'shared.rich-text': true,
      'shared.quote': true,
      'shared.articles-filter': true,
    },
  },
};

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: (params) =>
    qs.stringify(params as any, {
      encodeValuesOnly: true,
    }),
});

function getRuntimeStrapiBaseFromMeta(): string {
  if (typeof document === 'undefined') return '';
  const el = document.querySelector('meta[name="strapi-public-url"]');
  const c = el?.getAttribute('content')?.trim();
  if (!c) return '';
  return c.replace(/\/$/, '').replace(/\/api\/?$/, '');
}

/** Якщо сторінка по HTTPS, а Strapi віддав http:// той самий хост — уникаємо mixed content. */
function forceHttpsIfPageIsSecure(url: string, expectedHost: string): string {
  if (typeof window === 'undefined' || window.location.protocol !== 'https:') return url;
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:') return url;
    if (expectedHost && u.hostname !== expectedHost) return url;
    u.protocol = 'https:';
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Єдине місце для URL картинок/файлів з Strapi.
 * - відносний шлях (/uploads/...) → STRAPI_BASE_URL з REACT_APP_API_URL або meta strapi-public-url
 * - localhost у повному URL → переписується на base (старі записи в БД)
 * - http на тому ж хості що й HTTPS API → піднімається до https (mixed content)
 */
export function resolveStrapiMediaUrl(urlOrPath: string | null | undefined): string {
  if (urlOrPath == null) return '';
  const s = String(urlOrPath).trim();
  if (!s) return '';

  let base = STRAPI_BASE_URL.replace(/\/$/, '');
  if (!base) {
    base = getRuntimeStrapiBaseFromMeta().replace(/\/$/, '');
  }

  let expectedHost = '';
  try {
    if (base) expectedHost = new URL(base.startsWith('http') ? base : `https://${base}`).hostname;
  } catch {
    /* ignore */
  }

  if (/^https?:\/\//i.test(s)) {
    let out = s;
    if (base) {
      try {
        const parsed = new URL(s);
        if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
          const rest = `${parsed.pathname}${parsed.search}${parsed.hash}`;
          out = `${base}${rest.startsWith('/') ? rest : `/${rest}`}`;
        }
      } catch {
        /* ignore */
      }
    }
    out = forceHttpsIfPageIsSecure(out, expectedHost);
    return out;
  }

  if (s.startsWith('//')) {
    let out: string;
    if (base) {
      try {
        out = `${new URL(base).protocol}${s}`;
      } catch {
        out = `https:${s}`;
      }
    } else {
      out = `https:${s}`;
    }
    out = forceHttpsIfPageIsSecure(out, expectedHost);
    return out;
  }

  const path = s.startsWith('/') ? s : `/${s}`;
  const joined = base ? `${base}${path}` : path;
  return forceHttpsIfPageIsSecure(joined, expectedHost);
}

type StrapiSlugCollection = 'pages' | 'articles';

/** Дозаповнює slug для зв'язку page/article (id, documentId, об'єкт без slug). */
async function resolveStrapiEntry(
  client: typeof api,
  collection: StrapiSlugCollection,
  ref: any
): Promise<any | null> {
  if (ref == null) return null;

  const fetchOne = async (key: string | number) => {
    try {
      const { data } = await client.get(
        `/${collection}/${encodeURIComponent(String(key))}?fields[0]=slug&fields[1]=title`
      );
      return data?.data ?? null;
    } catch {
      return null;
    }
  };

  if (typeof ref === 'number') {
    return (await fetchOne(ref)) ?? null;
  }
  if (typeof ref === 'string' && /^\d+$/.test(ref)) {
    return (await fetchOne(ref)) ?? null;
  }
  if (typeof ref === 'object' && ref !== null) {
    const o = ref.data != null && typeof ref.data === 'object' ? ref.data : ref;
    if (o && typeof o === 'object' && o.slug) {
      return o;
    }
    if (o && typeof o === 'object' && o.documentId != null && o.documentId !== '') {
      const merged = await fetchOne(o.documentId);
      if (merged) return { ...o, ...merged };
    }
    if (o && typeof o === 'object' && o.id != null) {
      const merged = await fetchOne(o.id);
      if (merged) return { ...o, ...merged };
    }
    return o;
  }
  return null;
}

export async function enrichNavigationMenuItem(item: any): Promise<any> {
  if (item.page && typeof item.page === 'object' && item.page.data != null) {
    item.page = item.page.data;
  }
  if (item.article && typeof item.article === 'object' && item.article.data != null) {
    item.article = item.article.data;
  }
  if (item.page != null) {
    const resolved = await resolveStrapiEntry(api, 'pages', item.page);
    if (resolved) item.page = resolved;
  }
  if (item.article != null) {
    const resolved = await resolveStrapiEntry(api, 'articles', item.article);
    if (resolved) item.article = resolved;
  }
  return item;
}

/** Strapi інколи віддає { id, attributes: { title, slug, ... } } замість плоского об'єкта. */
export function normalizeStrapiDocument(raw: any): any {
  if (!raw || typeof raw !== 'object') return raw;
  if (
    raw.attributes != null &&
    typeof raw.attributes === 'object' &&
    !Array.isArray(raw.attributes)
  ) {
    const { id, documentId, attributes } = raw;
    return { id, documentId, ...attributes };
  }
  return raw;
}

// Types for our Strapi data
export interface StrapiResponse<T> {
  data: T;
  meta: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

// Strapi 5 структура (без attributes обгортки)
export interface Article {
  id: number;
  documentId?: string;
  title: string;
  description: string;
  slug: string;
  cover?: {
    id: number;
    documentId?: string;
    url: string;
    alternativeText?: string | null;
    width?: number;
    height?: number;
    name?: string;
    ext?: string;
    mime?: string;
    size?: number;
  };
  author?: {
    id: number;
    documentId?: string;
    name: string;
    slug?: string;
    email: string;
    avatar?: {
      id: number;
      url: string;
      alternativeText?: string | null;
    };
  };
  // Backward compatibility: old single relation `category`
  category?: {
    id: number;
    documentId?: string;
    name: string;
    slug: string;
    description: string;
  };
  categories?: Category[];
  tags?: TagItem[];
  blocks?: any[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface Author {
  id: number;
  documentId?: string;
  name: string;
  slug?: string;
  email: string;
  avatar?: {
    id: number;
    url: string;
    alternativeText?: string | null;
  };
  articles?: Article[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Category {
  id: number;
  documentId?: string;
  name: string;
  slug: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TagItem {
  name: string;
}

// API functions
export const apiService = {
  // Get all articles with populated relations
  getArticles: async (): Promise<StrapiResponse<Article[]>> => {
    const response = await api.get('/articles', { params: { populate: ARTICLE_REST_POPULATE } });
    return response.data;
  },

  // Get article by ID (fallback if slug doesn't work)
  getArticleById: async (id: number | string): Promise<StrapiResponse<Article>> => {
    const response = await api.get(`/articles/${id}`, { params: { populate: ARTICLE_REST_POPULATE } });
    return response.data;
  },

  // Get single article by slug
  getArticleBySlug: async (slug: string): Promise<StrapiResponse<Article>> => {
    try {
      // Спробуємо різні формати запиту для Strapi 5
      let response;

      // Варіант 1: Стандартний формат фільтрів
      try {
        response = await api.get('/articles', {
          params: {
            'filters[slug][$eq]': slug,
            populate: ARTICLE_REST_POPULATE,
          },
        });
      } catch (err: any) {
        // Варіант 2: Альтернативний формат
        response = await api.get('/articles', {
          params: {
            'filters[slug]': slug,
            populate: ARTICLE_REST_POPULATE,
          },
        });
      }

      // Strapi 5 returns an array when using filters, so we need to get the first item
      let data;
      if (Array.isArray(response.data.data)) {
        if (response.data.data.length === 0) {
          // Fallback: отримати всі статті і знайти за slug
          const allArticlesResponse = await api.get('/articles', {
            params: { populate: ARTICLE_REST_POPULATE },
          });
          if (Array.isArray(allArticlesResponse.data.data)) {
            // Strapi 5: slug безпосередньо в об'єкті, не в attributes
            const foundArticle = allArticlesResponse.data.data.find(
              (article: Article) => article.slug === slug
            );
            data = foundArticle || null;
          } else {
            data = null;
          }
        } else {
          data = response.data.data[0];
        }
      } else if (response.data.data) {
        data = response.data.data;
      } else {
        data = null;
      }

      return {
        ...response.data,
        data
      };
    } catch (error: any) {
      // Fallback: отримати всі статті і знайти за slug
      try {
        const allArticlesResponse = await api.get('/articles', {
          params: { populate: ARTICLE_REST_POPULATE },
        });
        if (Array.isArray(allArticlesResponse.data.data)) {
          // Strapi 5: slug безпосередньо в об'єкті, не в attributes
          const foundArticle = allArticlesResponse.data.data.find(
            (article: Article) => article.slug === slug
          );
          if (foundArticle) {
            return {
              ...allArticlesResponse.data,
              data: foundArticle
            };
          }
        }
      } catch (fallbackError) {
        // Fallback failed
      }

      throw error;
    }
  },

  // Search articles by title (case-insensitive, partial match)
  searchArticlesByTitle: async (query: string): Promise<StrapiResponse<Article[]>> => {
    const trimmed = (query || '').trim();
    if (!trimmed) {
      return { data: [], meta: {} };
    }
    try {
      const response = await api.get('/articles', {
        params: {
          'filters[title][$containsi]': trimmed,
          populate: ARTICLE_REST_POPULATE,
        },
      });
      return response.data;
    } catch (error: any) {
      try {
        const allResponse = await api.get('/articles', { params: { populate: ARTICLE_REST_POPULATE } });
        const list = Array.isArray(allResponse.data?.data) ? allResponse.data.data : [];
        const lower = trimmed.toLowerCase();
        const filtered = list.filter((a: Article) => (a.title || '').toLowerCase().includes(lower));
        return { ...allResponse.data, data: filtered, meta: allResponse.data?.meta || {} };
      } catch (fallbackError) {
        throw error;
      }
    }
  },

  // Get all authors
  getAuthors: async (): Promise<StrapiResponse<Author[]>> => {
    const response = await api.get('/authors?populate=*');
    return response.data;
  },

  // Get all categories
  getCategories: async (): Promise<StrapiResponse<Category[]>> => {
    try {
      const response = await api.get('/categories?populate=*');
      // Strapi 5: дані безпосередньо в data, не в attributes
      if (response.data.data && Array.isArray(response.data.data)) {
        // Конвертуємо зі Strapi 4 структури (якщо є attributes) в Strapi 5
        const processedCategories = response.data.data.map((cat: any) => {
          if (cat.attributes) {
            // Strapi 4 структура
            return {
              id: cat.id,
              name: cat.attributes.name,
              slug: cat.attributes.slug,
              description: cat.attributes.description || '',
            };
          } else {
            // Strapi 5 структура
            return {
              id: cat.id,
              documentId: cat.documentId,
              name: cat.name,
              slug: cat.slug,
              description: cat.description || '',
              createdAt: cat.createdAt,
              updatedAt: cat.updatedAt,
            };
          }
        });
        return {
          ...response.data,
          data: processedCategories,
        };
      }
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  // Get articles by category
  getArticlesByCategory: async (categorySlug: string): Promise<StrapiResponse<Article[]>> => {
    try {
      const response = await api.get('/articles', {
        params: {
          'filters[categories][slug][$eq]': categorySlug,
          populate: ARTICLE_REST_POPULATE,
        },
      });
      return response.data;
    } catch (error: any) {
      // Fallback: спробуємо отримати всі статті і відфільтрувати на клієнті
      try {
        const allArticlesResponse = await api.get('/articles', {
          params: { populate: ARTICLE_REST_POPULATE },
        });
        if (Array.isArray(allArticlesResponse.data.data)) {
          const filteredArticles = allArticlesResponse.data.data.filter((article: Article) => {
            const categories =
              Array.isArray(article.categories) && article.categories.length > 0
                ? article.categories
                : article.category
                  ? [article.category as Category]
                  : [];
            if (categories.some((cat) => typeof cat === 'object' && cat?.slug === categorySlug)) {
              return true;
            }
            return false;
          });
          return {
            ...allArticlesResponse.data,
            data: filteredArticles,
          };
        }
      } catch (fallbackError) {
        // Fallback failed
      }
      throw error;
    }
  },

  // Get single author by slug
  getAuthorBySlug: async (slugOrId: string): Promise<Author | null> => {
    const s = (slugOrId || '').trim();
    if (!s) return null;
    try {
      if (/^\d+$/.test(s)) {
        const byId = await api.get(`/authors/${s}?populate=*`);
        const row = byId.data?.data;
        return row ? (normalizeStrapiDocument(row) as Author) : null;
      }
      const response = await api.get('/authors', {
        params: {
          'filters[slug][$eq]': s,
          populate: '*',
          'pagination[pageSize]': 1,
        },
      });
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      if (rows.length === 0) return null;
      return normalizeStrapiDocument(rows[0]) as Author;
    } catch {
      return null;
    }
  },

  // Get all articles by author slug
  getArticlesByAuthor: async (authorSlugOrId: string): Promise<StrapiResponse<Article[]>> => {
    const s = (authorSlugOrId || '').trim();
    if (!s) return { data: [], meta: {} };
    try {
      const params: Record<string, unknown> = {
        populate: ARTICLE_REST_POPULATE,
        sort: 'publishedAt:desc',
      };
      if (/^\d+$/.test(s)) {
        params['filters[author][id][$eq]'] = s;
      } else {
        params['filters[author][slug][$eq]'] = s;
      }
      const response = await api.get('/articles', { params });
      return response.data;
    } catch {
      // Fallback: client-side filter if backend filter format differs
      const all = await api.get('/articles', {
        params: { populate: ARTICLE_REST_POPULATE, sort: 'publishedAt:desc' },
      });
      const rows = Array.isArray(all.data?.data) ? all.data.data : [];
      const filtered = rows.filter((a: Article) =>
        /^\d+$/.test(s) ? String(a.author?.id) === s : a.author?.slug === s
      );
      return { ...all.data, data: filtered };
    }
  },

  // Get all articles by hashtag (tag component: shared.tag-item.name)
  getArticlesByTag: async (tagName: string): Promise<StrapiResponse<Article[]>> => {
    const tag = (tagName || '').trim();
    if (!tag) return { data: [], meta: {} };
    try {
      const response = await api.get('/articles', {
        params: {
          'filters[tags][name][$eqi]': tag,
          populate: ARTICLE_REST_POPULATE,
          sort: 'publishedAt:desc',
        },
      });
      return response.data;
    } catch {
      const all = await api.get('/articles', {
        params: { populate: ARTICLE_REST_POPULATE, sort: 'publishedAt:desc' },
      });
      const rows = Array.isArray(all.data?.data) ? all.data.data : [];
      const lower = tag.toLowerCase();
      const filtered = rows.filter((a: Article) =>
        Array.isArray(a.tags) && a.tags.some((t) => (t?.name || '').toLowerCase() === lower)
      );
      return { ...all.data, data: filtered };
    }
  },


  // Get global settings
  getGlobal: async (): Promise<StrapiResponse<any>> => {
    const response = await api.get('/global?populate=*');
    return response.data;
  },

  // Get navigation menu by slug or ID
  getNavigation: async (slugOrId: string): Promise<any[]> => {
    if (!slugOrId) {
      return [];
    }

    try {
      // Спробуємо різні варіанти запитів
      let response;

      // Спочатку спробуємо через ID (якщо slugOrId - число)
      const id = parseInt(slugOrId);
      if (!isNaN(id)) {
        // Спробуємо різні варіанти populate для завантаження page в компонентах
        try {
          // Варіант 1: Детальний populate для компонентів
          try {
            response = await api.get(`/navigations/${id}?populate[items][populate][page]=*&populate[items][populate][article]=*`);
          } catch (err1: any) {
            // Варіант 2: Простіший populate
            try {
              response = await api.get(`/navigations/${id}?populate[items][populate]=*`);
            } catch (err2: any) {
              // Варіант 3: Найпростіший populate
              response = await api.get(`/navigations/${id}?populate=*`);
            }
          }
          if (response.data.data) {
            const navData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
            const items = navData?.items || [];
            if (items.length > 0) {
              const processedItems = await Promise.all(
                items.map((item: any) => enrichNavigationMenuItem(item))
              );
              return processedItems;
            }
          }
        } catch (err1: any) {
        }
      }

      // Якщо ID не працює або slugOrId - не число, спробуємо через slug
      // Спробуємо різні варіанти populate для завантаження page в компонентах
      try {
        // Варіант 1: Детальний populate для компонентів
        try {
          response = await api.get(`/navigations?filters[slug][$eq]=${slugOrId}&populate[items][populate][page]=*&populate[items][populate][article]=*`);
        } catch (err1: any) {
          // Варіант 2: Простіший populate
          try {
            response = await api.get(`/navigations?filters[slug][$eq]=${slugOrId}&populate[items][populate]=*`);
          } catch (err2: any) {
            // Варіант 3: Найпростіший populate
            response = await api.get(`/navigations?filters[slug][$eq]=${slugOrId}&populate=*`);
          }
        }

        if (response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const items = response.data.data[0].items || [];
          if (items.length > 0) {
            const processedItems = await Promise.all(
              items.map((item: any) => enrichNavigationMenuItem(item))
            );
            return processedItems;
          }
        }
      } catch (err3: any) {
        // Request failed
      }

      return [];
    } catch (error: any) {
      return [];
    }
  },


  // Get footer settings
  getFooterSettings: async (): Promise<any> => {
    try {
      // Для завантаження вкладених компонентів (items) потрібен детальний populate
      // Спробуємо різні варіанти populate-запитів
      let response;

      // Варіант 1: навігація + компонент socialLinks
      try {
        response = await api.get(
          '/footer?populate[navigation][populate]=*&populate[socialLinks]=*'
        );
      } catch (err: any) {
        // Варіант 2: populate=* (усі перші рівні, включно з компонентами)
        try {
          response = await api.get('/footer?populate=*');
        } catch (errPop: any) {
        // Варіант 3: окремі populate
        try {
          response = await api.get('/footer?populate[navigation]=*');
        } catch (err2: any) {
          // Варіант 4: вкладений populate
          try {
            response = await api.get('/footer?populate[navigation][populate]=*');
          } catch (err3: any) {
            try {
              response = await api.get('/footer');
            } catch (err4: any) {
              throw err4;
            }
          }
        }
        }
      }

      if (response.data.data) {
        const footerData = response.data.data;

        // Обробляємо navigation items
        if (footerData.navigation) {
          // Якщо items не завантажені, спробуємо завантажити їх окремо
          if (!footerData.navigation.items || !Array.isArray(footerData.navigation.items)) {
            try {
              // Спробуємо різні варіанти endpoint
              let navResponse;
              try {
                navResponse = await api.get(`/navigations/${footerData.navigation.id}?populate=*`);
              } catch (err1: any) {
                try {
                  navResponse = await api.get(`/navigation/${footerData.navigation.id}?populate=*`);
                } catch (err2: any) {
                  // Спробуємо через filters
                  navResponse = await api.get(`/navigations?filters[id][$eq]=${footerData.navigation.id}&populate=*`);
                }
              }

              if (navResponse) {
                // Обробляємо різні формати відповіді
                let navData = null;
                if (navResponse.data.data) {
                  if (Array.isArray(navResponse.data.data) && navResponse.data.data.length > 0) {
                    navData = navResponse.data.data[0];
                  } else if (typeof navResponse.data.data === 'object') {
                    navData = navResponse.data.data;
                  }
                }

                if (navData && navData.items) {
                  footerData.navigation.items = navData.items;
                }
              }
            } catch (navError: any) {
              // Error fetching navigation separately
            }
          }

          // Обробляємо navigation items
          if (footerData.navigation.items && Array.isArray(footerData.navigation.items)) {
            const processedItems = await Promise.all(
              footerData.navigation.items.map(async (item: any) => {
                // Обробляємо page
                if (item.page) {
                  // Якщо page - це число (id), завантажуємо повний об'єкт
                  if (typeof item.page === 'number') {
                    try {
                      const pageResponse = await api.get(`/pages/${item.page}?fields[0]=slug&fields[1]=title`);
                      if (pageResponse.data.data) {
                        item.page = pageResponse.data.data;
                      }
                    } catch (err) {
                      // Error fetching page
                    }
                  } else if (typeof item.page === 'object' && item.page !== null) {
                    // Якщо page - об'єкт, перевіряємо структуру
                    if (item.page.data) {
                      item.page = item.page.data;
                    }
                    // Якщо page має id але немає slug, завантажуємо slug через API
                    if (item.page.id && !item.page.slug) {
                      try {
                        const pageResponse = await api.get(`/pages/${item.page.id}?fields[0]=slug&fields[1]=title`);
                        if (pageResponse.data.data && pageResponse.data.data.slug) {
                          item.page.slug = pageResponse.data.data.slug;
                        }
                      } catch (err) {
                        // Error fetching page slug
                      }
                    }
                  }
                }

                // Обробляємо article
                if (item.article && typeof item.article === 'object' && item.article.data) {
                  item.article = item.article.data;
                }

                return item;
              })
            );
            footerData.navigation.items = processedItems;
          }
        }

        return footerData;
      }
      return null;
    } catch (error: any) {
      return null;
    }
  },

  // Get header settings
  getHeaderSettings: async (): Promise<any> => {
    try {
      // Для завантаження вкладених компонентів (items) потрібен детальний populate
      // Спробуємо різні варіанти populate-запитів
      let response;

      // Варіант 1: найпростіший populate
      try {
        response = await api.get('/header?populate=*');
      } catch (err: any) {
        // Варіант 2: окремі populate
        try {
          response = await api.get('/header?populate[logo]=*&populate[navigation]=*');
        } catch (err2: any) {
          // Варіант 3: вкладений populate
          try {
            response = await api.get('/header?populate[logo]=*&populate[navigation][populate]=*');
          } catch (err3: any) {
            // Спробуємо без populate, щоб хоча б отримати базові дані
            try {
              response = await api.get('/header');
            } catch (err4: any) {
              throw err4;
            }
          }
        }
      }

      if (response.data.data) {
        const headerData = response.data.data;

        // Обробляємо navigation items
        if (headerData.navigation) {
          // Якщо items не завантажені, спробуємо завантажити їх окремо
          if (!headerData.navigation.items || !Array.isArray(headerData.navigation.items) || headerData.navigation.items.length === 0) {
            const slugOrId = headerData.navigation.slug ||
                            headerData.navigation.id?.toString() ||
                            headerData.navigation.documentId ||
                            '';

            if (slugOrId) {
              try {
                const navItems = await apiService.getNavigation(slugOrId);
                if (navItems && Array.isArray(navItems) && navItems.length > 0) {
                  headerData.navigation.items = navItems;
                }
              } catch (navError: any) {
              }
            }
          } else {
          }

          // Обробляємо navigation items
          if (headerData.navigation.items && Array.isArray(headerData.navigation.items)) {
            const processedItems = await Promise.all(
              headerData.navigation.items.map((item: any) => enrichNavigationMenuItem(item))
            );
            headerData.navigation.items = processedItems;
          }
        }

        return headerData;
      }
      return null;
    } catch (error: any) {
      return null;
    }
  },

  // Get all categories
  getAllCategories: async (): Promise<any[]> => {
    try {
      const response = await api.get('/categories?populate=*');
      if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    } catch (error: any) {
      return [];
    }
  },

  // Get all articles
  getAllArticles: async (): Promise<Article[]> => {
    try {
      const response = await api.get('/articles', {
        params: { populate: ARTICLE_REST_POPULATE, sort: 'publishedAt:desc' },
      });
      if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    } catch (error: any) {
      return [];
    }
  },

  // Get page by ID
  getPageById: async (id: number | string): Promise<any> => {
    try {
      const response = await api.get(`/pages/${id}`, { params: { populate: PAGE_REST_POPULATE } });
      if (response.data.data) {
        return { data: response.data.data };
      }
      return null;
    } catch (error: any) {
      return null;
    }
  },

  // Get page by slug (лише опубліковані, якщо у типу увімкнено Draft & Publish)
  getPageBySlug: async (slug: string): Promise<any> => {
    const s = (slug || '').trim();
    if (!s) return null;

    const pickFirst = (data: any): any[] => {
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object') return [data];
      return [];
    };

    const matchSlug = (raw: any): boolean => {
      const p = normalizeStrapiDocument(raw);
      return p?.slug === s;
    };

    try {
      let response;
      try {
        response = await api.get('/pages', {
          params: {
            'filters[slug][$eq]': s,
            populate: PAGE_REST_POPULATE,
            'pagination[page]': 1,
            'pagination[pageSize]': 10,
          },
        });
      } catch {
        response = await api.get('/pages', {
          params: {
            'filters[slug][$eq]': s,
            populate: PAGE_REST_POPULATE,
          },
        });
      }

      const rows = pickFirst(response?.data?.data);
      const hit = rows.find(matchSlug);
      if (hit) {
        return normalizeStrapiDocument(hit);
      }

      // Fallback: багато сторінок + фільтр не спрацював — тягнемо список з великим лімітом
      const allPagesResponse = await api.get('/pages', {
        params: {
          populate: PAGE_REST_POPULATE,
          'pagination[page]': 1,
          'pagination[pageSize]': 100,
        },
      });

      const allRows = pickFirst(allPagesResponse?.data?.data);
      const found = allRows.find(matchSlug);
      if (found) {
        return normalizeStrapiDocument(found);
      }

      return null;
    } catch {
      return null;
    }
  },
};

export default api;
